<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { getBathroomsRange, getBedroomsRange, getImageByPriority, getMinPrice } from '@/views/search/index.ts';
import { Constants } from '@/utilities/constants.ts';
import { useI18n } from 'vue-i18n';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faBath, faBed } from '@fortawesome/pro-solid-svg-icons';
import { getCustomIcon } from '@/common/custom-icons.ts';
import ProjectBadge from '@/components/ProjectBadge.vue';
import { getProjectCard, type GetEditorialNearbySearchResultsProjectResponse, type GetEditorialSearchResultsProjectResponse, type GetProjectCardResponse } from '@/api';
import { useSsgState } from '@/utilities/ssg-state.ts';

type SearchResultProject = GetProjectCardResponse | GetEditorialSearchResultsProjectResponse | GetEditorialNearbySearchResultsProjectResponse;

const props = defineProps<{
  index: number;
  projectId?: number;
  project?: SearchResultProject;
  highlighted?: boolean;
  priceTypeId?: number | null;
  compact?: boolean;
}>();

const { locale, t } = useI18n();

const project = ref<SearchResultProject | null>(props.project ?? null);

const loadProject = async () => {
  try {
    if (props.project) {
      project.value = props.project;
      return;
    }

    if (!props.projectId) {
      project.value = null;
      return;
    }

    const projectResult = await getProjectCard({
      path: {
        projectId: props.projectId,
      },
    });

    if (!projectResult.response?.ok || !projectResult.data) {
      throw new Error(`Failed to fetch project. ${JSON.stringify(projectResult.error)}`);
    }

    project.value = projectResult.data;
  } catch (err) {
    console.error(err);
  }
};

useSsgState({
  key: props.project === undefined && props.projectId !== undefined ? `project-card:${props.projectId}` : null,
  refs: { project },
  isLoaded: () => project.value !== null,
  load: loadProject,
});

watch(
  () => [props.project, props.projectId],
  () => {
    void loadProject();
  },
  { immediate: false }
);

const image = computed(() => (project.value ? getImageByPriority(project.value) : null));

const minPrice = computed(() => (project.value ? getMinPrice(project.value, props.priceTypeId) : undefined));
const bedroomsRange = computed(() => (project.value ? getBedroomsRange(project.value) : undefined));
const bathroomsRange = computed(() => (project.value ? getBathroomsRange(project.value) : undefined));

const hasCharacteristics = computed(() => {
  if (!project.value) return false;
  return bedroomsRange.value || bathroomsRange.value || project.value.tags.length > 0;
});

const displayTags = computed(() => (project.value ? project.value.tags.slice(0, 5) : []));

const fetchPriority = computed(() => (props.index === 0 ? 'high' : 'auto'));
const loadingPriority = computed(() => (props.index < 2 ? 'eager' : 'lazy'));
const searchResultSizes = `(max-width: ${Constants.Breakpoints.md}) 100vw, ${Constants.ImageSizes.Medium.Width}px`;
</script>

<template>
  <li v-if="project && image && compact" class="group relative grid grid-cols-[4.5rem_minmax(0,1fr)] items-center gap-3 py-2">
    <img :alt="image.title" :height="Constants.ImageSizes.Small.Height" :loading="loadingPriority" :src="image.src.resizedToSmall()" :width="Constants.ImageSizes.Small.Width" class="aspect-square w-full rounded-md object-cover" />
    <div class="min-w-0">
      <h3 class="group-hover:text-ph-green-700 truncate text-sm font-semibold text-gray-900">{{ project.name }}</h3>
      <p class="truncate text-xs text-gray-500">{{ project.address.city.name }}, {{ project.address.province }}</p>
      <p class="mt-0.5 text-xs">
        <template v-if="project.isPaid && minPrice">
          <span v-if="minPrice.isStarting" class="pr-1 text-gray-500">{{ t('views.search.results.starting-from') }}</span>
          <span class="text-ph-green-700 font-semibold">{{ minPrice.amount.formatCurrency(locale) }}</span>
          <span v-if="minPrice.type.id == Constants.PriceTypeId.ForRent" class="pl-1 text-gray-500">{{ t('views.search.results.per-month') }}</span>
        </template>
        <span v-else class="text-gray-500">{{ t('views.search.results.upon-request') }}</span>
      </p>
    </div>
    <RouterLink :to="{ name: 'project', params: { slug: project.name.toSlug(), projectId: project.id } }" class="focus-visible:ring-ph-green-500 absolute inset-0 rounded-md focus-visible:ring-2 focus-visible:outline-hidden">
      <span class="sr-only">{{ project.name }}</span>
    </RouterLink>
  </li>
  <li v-else-if="project && image" :class="['relative rounded-lg p-0.5 outline transition-shadow', project.isPaid ? 'outline-ph-green-300' : 'outline-gray-100', highlighted ? 'ring-ph-green-500 ring-2 ring-offset-2' : '']">
    <div class="aspect-h-2 aspect-w-3 block w-full overflow-hidden rounded-t-lg">
      <img
        :alt="image.title"
        :fetchpriority="fetchPriority"
        :height="Constants.ImageSizes.Medium.Height"
        :loading="loadingPriority"
        :sizes="searchResultSizes"
        :src="image.src.resizedToSmall()"
        :srcset="image.src.toSrcset()"
        :width="Constants.ImageSizes.Medium.Width"
        class="object-cover"
      />
      <div class="pointer-events-none absolute inset-0 p-2">
        <ProjectBadge :banners="project.banners" :isNew="project.isNew" :isPaid="project.isPaid" :isSold="project.isSold" />
      </div>
      <div v-if="project.isPaid" class="absolute inset-0">
        <div class="absolute right-0 bottom-0 h-auto w-1/4 overflow-hidden rounded-tl-lg border-4 border-gray-700/50">
          <div class="bg-white p-2">
            <img
              v-if="project.logo"
              :alt="project.name"
              :fetchpriority="fetchPriority"
              :height="Constants.ImageSizes.Small.Height"
              :loading="loadingPriority"
              :src="project.logo.resizedToSmall(true)"
              :width="Constants.ImageSizes.Small.Width"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="p-1">
      <h3 :class="[project.isPaid ? 'text-base/6 text-gray-900' : 'text-sm/6 text-gray-600', 'block truncate font-semibold tracking-tight']">
        {{ project.name }}
      </h3>
      <p class="mb-4 block text-xs font-medium text-gray-500">{{ project.address.city.name }}, {{ project.address.province }}</p>
      <ul v-if="hasCharacteristics" class="mb-4 flex max-h-8 flex-wrap gap-x-2 overflow-hidden text-sm">
        <li v-if="bedroomsRange" class="flex h-8 items-center rounded-md bg-gray-200 px-2 py-1.5">
          <FontAwesomeIcon :icon="faBed" class="mr-1 h-4 text-gray-500" />
          <span>
            <template v-if="bedroomsRange.hasStudio">
              {{ t('views.search.results.studio') }}<template v-if="bedroomsRange.max"> &ndash; {{ bedroomsRange.max }} </template>
            </template>
            <template v-else>
              {{ bedroomsRange.min }}<template v-if="bedroomsRange.max !== bedroomsRange.min"> &ndash; {{ bedroomsRange.max }} </template>
            </template>
          </span>
        </li>
        <li v-if="bathroomsRange" class="flex h-8 items-center rounded-md bg-gray-200 px-2 py-1.5">
          <FontAwesomeIcon :icon="faBath" class="mr-1 h-4 text-gray-500" />
          <span>
            {{ bathroomsRange.min }}<template v-if="bathroomsRange.max !== bathroomsRange.min"> &ndash; {{ bathroomsRange.max }} </template>
          </span>
        </li>
        <li v-for="item in displayTags" :key="item.id" class="flex h-8 items-center rounded-md bg-gray-200 px-2 py-1.5">
          <FontAwesomeIcon :icon="getCustomIcon(item.customIcon)" class="h-4 text-gray-500" />
        </li>
      </ul>
      <p :class="[hasCharacteristics ? '' : 'mt-16', 'text-right text-sm text-gray-800']">
        <template v-if="project.isPaid && minPrice">
          <span v-if="minPrice.isStarting" class="pr-1 text-xs text-gray-500">
            {{ t('views.search.results.starting-from') }}
          </span>
          <span class="font-semibold">
            {{ minPrice.amount.formatCurrency(locale) }}
          </span>
          <span v-if="minPrice.type.id == Constants.PriceTypeId.ForRent" class="pl-1 text-xs">
            {{ t('views.search.results.per-month') }}
          </span>
        </template>
        <template v-else>
          <span class="text-xs text-gray-500">
            {{ t('views.search.results.upon-request') }}
          </span>
        </template>
      </p>
    </div>

    <RouterLink :to="{ name: 'project', params: { slug: project.name.toSlug(), projectId: project.id } }" class="focus-within:ring-ph-green-500 absolute inset-0 cursor-pointer rounded-lg focus-within:ring-2 focus-within:outline-hidden">
      <span class="sr-only">{{ project.name }}</span>
    </RouterLink>
  </li>
</template>

<style lang="css" scoped></style>
